import React, {useState} from "react";
import {Button, Col, DatePicker, Input, message, Modal, Row, Space, Table, Upload} from "antd";
import {DownloadOutlined, UploadOutlined} from "@ant-design/icons";
import {useStore} from "../../../hooks/useStore";
import {b64DecodeToUrl} from "../../../utils/helpers";
import moment from "moment";
import {useFetch} from "hooks/useFetch";
import {AdjustmentTypeFilter, DebtStatusFilter} from "../../_defaultComponents/FilterComponents";
import {Shortener} from "components/_defaultComponents/Shortener/Shortener";

export const CourierAdjustmentsTab = ({isTabActive = false}) => {
    const stores = useStore();

    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        tckn: "",
        fullname: "",
        adjustmentTypeId: "",
        isProcessed: undefined,
        skip: 0,
        limit: 20
    });
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [importLoading, setImportLoading] = useState(false);

    const [
        loadingCourierAdjustments,
        courierAdjustments,
        _,
        __,
        refetchCourierAdjustments
    ] = useFetch({
        fetchFn: stores.courierAdjustmentStore.getCourierAdjustments.bind(null, {
            ...filters,
            startDate: filters.startDate
                ? moment(filters.startDate).format("YYYY-MM-DD")
                : undefined,
            endDate: filters.endDate ? moment(filters.endDate).format("YYYY-MM-DD") : undefined
        }),
        dependencies: [JSON.stringify(filters), isTabActive],
        condition: isTabActive,
        initialCall: true
    });

    const handleDownloadTemplate = async () => {
        try {
            const resp = await stores.courierAdjustmentStore.getCourierAdjustmentsTemplate();
            if (resp?.success && resp.data) {
                const url = await b64DecodeToUrl(resp.data.result);
                const link = document.createElement("a");
                link.href = url;
                link.download = `Kurye_Eklenti_Kesinti_Sablonu.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => window.URL.revokeObjectURL(url), 200);
                message.success("Şablon dosyası başarıyla indirildi.");
            } else {
                message.error("Şablon linki alınamadı.");
            }
        } catch (e) {
            console.error(e);
            message.error("Şablon linki yüklenirken hata oluştu.");
        }
    };

    const handleExport = async () => {
        try {
            const response = await stores.courierAdjustmentStore.getCourierAdjustments({
                ...filters,
                startDate: filters.startDate
                    ? moment(filters.startDate).format("YYYY-MM-DD")
                    : undefined,
                endDate: filters.endDate ? moment(filters.endDate).format("YYYY-MM-DD") : undefined,
                excel: true
            });
            if (response?.success && response?.data) {
                const url = await b64DecodeToUrl(response?.data?.result);
                const link = document.createElement("a");
                link.href = url;
                link.download = `Kurye_Eklentiler_Kesintiler_${moment().format(
                    "YYYY-MM-DD_HH-mm-ss"
                )}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => window.URL.revokeObjectURL(url), 200);
                message.success("Excel dosyası başarıyla indirildi.");
            } else {
                message.error("Excel dosyası oluşturulamadı.");
            }
        } catch (err) {
            console.error(err);
            message.error("Excel export hatası oluştu.");
        }
    };

    const fileUploadProps = {
        name: "file",
        multiple: false,
        accept: ".xlsx,.xls,.csv",
        showUploadList: false,
        customRequest: async ({file, onSuccess, onError}) => {
            setImportLoading(true);
            try {
                const formData = new FormData();
                formData.append("file", file);
                const response = await stores.courierAdjustmentStore.uploadCourierAdjustments(
                    formData
                );

                if (response?.data?.errorExcel) {
                    const url = await b64DecodeToUrl(response?.data?.errorExcel);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `Hata_Raporu_${file.name}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setTimeout(() => window.URL.revokeObjectURL(url), 200);
                }

                if (response?.data?.success === true) {
                    stores.appStore.openSuccessModal({
                        title: "Kurye Eklenti/Kesinti dosyası başarıyla yüklendi!"
                    });
                    refetchCourierAdjustments();
                } else if (response?.data?.success === false) {
                    stores.appStore.openErrorModal({
                        title: "Yükleme sırasında satır hataları bulundu",
                        content: `Hata raporu otomatik olarak indirildi.`
                    });
                } else {
                    message.error(response.data?.message || "Dosya yüklenirken bir hata oluştu.");
                }
                onSuccess(null, file);
                setUploadModalVisible(false);
            } catch (err) {
                console.error(err);
                onError(err);
                message.error(`${file.name} yüklenirken hata oluştu.`);
            } finally {
                setImportLoading(false);
            }
        }
    };

    const columns = [
        {
            title: "Tarih",
            dataIndex: "date",
            key: "date",
            render: date => (date ? moment(date).format("DD/MM/YYYY") : "-")
        },
        {
            title: "Kurye TC Kimlik",
            dataIndex: ["courier", "tckn"],
            key: "tckn"
        },
        {
            title: "Kurye Adı Soyadı",
            dataIndex: ["courier", "fullname"],
            key: "courierName"
        },
        {
            title: "Eklenti/Kesinti Tipi",
            dataIndex: "adjustmentType",
            key: "adjustmentType"
        },
        {
            title: "KDV Oranı",
            dataIndex: "kdvRate",
            key: "kdvRate",
            render: val => `${val || 0} %`
        },
        {
            title: "Ekipman Mı?",
            dataIndex: "isEquipment",
            key: "isEquipment",
            render: val => (val ? "Evet" : "Hayır")
        },
        {
            title: "Açıklama",
            dataIndex: "description",
            key: "description",
            render: val => (
                <Shortener text={val} length={32} showTitle={true} render={text => text} />
            )
        },
        {
            title: "Eklenecek/Kesilecek Tutar",
            dataIndex: "totalAmount",
            key: "totalAmount",
            render: val => `${val || 0} ₺`
        },
        {
            title: "Eklenen/Kesilen Tutar",
            dataIndex: "processedAmount",
            key: "processedAmount",
            render: val => `${val || 0} ₺`
        },
        {
            title: "Kalan Tutar",
            dataIndex: "remainingAmount",
            key: "remainingAmount",
            render: val => `${val || 0} ₺`
        },
        {
            title: "Taksit Sayısı",
            dataIndex: "installmentCount",
            key: "installmentCount",
            render: (val, record) =>
                record?.processedInstallmentCount && record?.installmentCount
                    ? record?.processedInstallmentCount + "/" + record?.installmentCount
                    : "-"
        },
        {
            title: "Borç Durumu",
            dataIndex: "isProcessed",
            key: "isProcessed",
            render: val => (val === true ? "Tamamlanmış" : val === false ? "Tamamlanmamış" : "-")
        }
    ];

    return (
        <>
            <Row gutter={[16, 16]} style={{marginBottom: 16}} align="middle">
                <Col>
                    {/* date range picker */}
                    <DatePicker.RangePicker
                        placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
                        value={
                            filters.startDate && filters.endDate
                                ? [filters.startDate, filters.endDate]
                                : []
                        }
                        onChange={dates =>
                            setFilters({
                                ...filters,
                                startDate: dates?.[0],
                                endDate: dates?.[1],
                                skip: 0
                            })
                        }
                        format="DD/MM/YYYY"
                    />
                </Col>
                <Col>
                    <Input
                        placeholder="TCKN"
                        value={filters.tckn}
                        onChange={e => setFilters({...filters, tckn: e.target.value, skip: 0})}
                        allowClear
                        style={{width: 200}}
                    />
                </Col>
                <Col>
                    <Input
                        placeholder="Ad Soyad"
                        value={filters.fullname}
                        onChange={e => setFilters({...filters, fullname: e.target.value, skip: 0})}
                        allowClear
                        style={{width: 200}}
                    />
                </Col>
                <Col style={{width: 220}}>
                    <AdjustmentTypeFilter
                        value={filters.adjustmentTypeId || undefined}
                        onChange={value =>
                            setFilters({
                                ...filters,
                                adjustmentTypeId: value === "all" ? "" : value,
                                skip: 0
                            })
                        }
                        showLabel={false}
                        showAllOption={false}
                        marginBottom="0px"
                        placeholder="Eklenti/Kesinti Tipi"
                    />
                </Col>
                <Col style={{width: 200}}>
                    <DebtStatusFilter
                        value={filters.isProcessed}
                        onChange={v =>
                            setFilters({
                                ...filters,
                                isProcessed: v,
                                // Borç durumu seçilince tarih filtresi temizlenir
                                ...(typeof v === "boolean" && {startDate: null, endDate: null}),
                                skip: 0
                            })
                        }
                        showLabel={false}
                        marginBottom="0px"
                        placeholder="Borç Durumu"
                    />
                </Col>
                <Col flex="auto" style={{textAlign: "right"}}>
                    <Space>
                        <Button
                            type="default"
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadTemplate}
                        >
                            Şablon İndir
                        </Button>
                        <Button
                            type="default"
                            icon={<UploadOutlined />}
                            onClick={() => setUploadModalVisible(true)}
                        >
                            Excel Yükle
                        </Button>
                        <Button type="primary" onClick={handleExport}>
                            Excel Çıktı Al
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={courierAdjustments?.data?.result || []}
                rowKey="_id"
                loading={loadingCourierAdjustments}
                scroll={{x: 1200}}
                size="small"
                pagination={{
                    pageSize: filters.limit,
                    total: courierAdjustments?.data?.dataCount || 0,
                    current: Math.floor(filters.skip / filters.limit) + 1,
                    showTotal: total => `Toplam ${total} kayıt`,
                    onChange: (page, pageSize) => {
                        setFilters({...filters, skip: (page - 1) * pageSize, limit: pageSize});
                    }
                }}
            />

            <Modal
                title="Excel Yükle"
                visible={uploadModalVisible}
                confirmLoading={importLoading}
                onCancel={() => setUploadModalVisible(false)}
                footer={null}
            >
                <Upload {...fileUploadProps}>
                    <Button icon={<UploadOutlined />}>Dosya Seç</Button>
                </Upload>
            </Modal>
        </>
    );
};
